import { useEffect, useRef, useState } from "react";
import styles from "../../styles/patient-page-modules/Monitoring.module.css";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { createMonitoringSession } from "../../services/patient/details";
import { getPatientLocationId } from "../../utils/helper";
import { refineNote } from "../../services/patient/carePlan";
import NoteAutocompleteTextarea from "../ui/note-autocomplete";
import Button from "../button-v1";
import Tooltip from "../shared-modules/Tooltip";
import { handleApiError } from "../../utils/handleApiError";
import clsx from "clsx";
import { getNoteTemplates } from "../../services/location";
import magicIcon from "../../assets/images/magic.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import DatePicker from "../../components/shared-modules/DatePicker/DatePicker"; // Import DatePicker
import useCurrentTime from "../../components/shared-modules/DatePicker/useCurrentTime"; // Import useCurrentTime
import { toUTCISOString } from "../../utils/dateFormatters";
import SaveSessionToast from "./SaveSessionToast";
import NotesHistoryDrawer from "./notes/NotesHistoryDrawer";

const Monitoring = ({
  sessionId,
  ccmSessionId,
  resetTimer,
  isOpen,
  onClose,
  defaultTimer,
  noteToPaste,
  // May 2026 redesign: allow caller to override the section title
  // ("RPM Monitoring" / "CCM Monitoring") and to flatten the layout so
  // the time-options + spinner controls render BELOW the title instead
  // of overflowing the CollapsableSection's actions slot when the panel
  // sits in the narrow right column.
  title = "Monitoring",
  inlineActions = false,
  chatBotType = null,
  // Optional patientId prop - when provided, takes precedence over sessionStorage
  // Used by DailiesRightPanel to ensure correct patient context during navigation
  patientId: propPatientId,
  // Shows the Notes History icon button + drawer in the heading row.
  // Opt-in (currently just the /my-tasks "Notes" card via DailiesRightPanel)
  // so other Monitoring/RPM-Monitoring/CCM-Monitoring call sites are unaffected.
  showNotesHistory = false,
}) => {
  const accountId = sessionStorage.getItem("acc_id");
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const [isNotesHistoryOpen, setIsNotesHistoryOpen] = useState(false);
  const notesHistoryBtnRef = useRef(null);

  // Use propPatientId if provided, otherwise fall back to sessionStorage
  const patientId = propPatientId || sessionStorage.getItem("_pt_id");
  const queryClient = useQueryClient();
  const [queryParams] = useSearchParams();

  // June 2026 time-tracking — snapshot + additive-manual model.
  // ------------------------------------------------------------
  // First click on the field captures the live timer into snapshotValue
  // (rendered in the heading chip). The field itself stays EMPTY for
  // the user to type the time they want to ADD on top of the snapshot.
  // Save total = snapshot + manual.
  //
  // Earlier iterations pre-filled the field with the snapshot value,
  // which made typing visually OVERWRITE the snapshot — leading to a
  // confusing toast where typing "12:06" produced a Manual row of
  // "+12:04" (= 12:06 total minus 0:02 snapshot). Splitting snapshot
  // (chip) from manual (field) makes the toast's Manual row equal
  // exactly what the user typed.
  //
  // The minutes and seconds boxes are independent raw-digit fields (empty
  // string = not typed yet). Keeping them separate lets the seconds box hold
  // values >59 (e.g. 80, 120) without rolling into minutes; they are only
  // summed at save time. 'Engaged' is tracked by snapshotValue, captured on
  // first focus, so save uses snapshot + manual when engaged, else liveSeconds.
  const [manualMin, setManualMin] = useState("");
  const [manualSec, setManualSec] = useState("");
  const [snapshotValue, setSnapshotValue] = useState(null);
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);

  const [noteType, setNoteType] = useState(
    chatBotType === "CCM" ? "ccm_note" : "rpm_note"
  );

  // Keep noteType in sync when the top-level tab changes; reset manual
  // entry so a typed value from one program doesn't leak into the other.
  useEffect(() => {
    if (chatBotType) {
      setNoteType(chatBotType === "CCM" ? "ccm_note" : "rpm_note");
      setManualMin("");
      setManualSec("");
      setSnapshotValue(null);
    }
  }, [chatBotType]);

  const [notes, setNotes] = useState("");

  // Date State
  const curr = useCurrentTime();
  const [selectedDate, setSelectedDate] = useState(null);

  const [complicanCall, setComplianceCall] = useState(false);
  const [callCompleted, setCallCompleted] = useState(false);
  const [twoWayCommunicationCall, setTwoWayCommunicationCall] = useState(false);
  // Last-clicked communication type, sent as `communication_outcome` on the
  // Refine Note API. Independent of the booleans above (which drive the
  // notes-append text and the session-save payload) because those can't
  // represent click order — and the API contract wants a single value.
  const [communicationOutcome, setCommunicationOutcome] = useState(null);

  useEffect(() => {
    if (noteToPaste && noteToPaste.text) {
      setNotes((prev) => (prev ? `${prev}\n${noteToPaste.text}` : noteToPaste.text));
    }
  }, [noteToPaste]);

  const myTasksActiveTab = sessionStorage.getItem("my-tasks/active-tab");

  const CCM = Boolean(parseInt(queryParams.get("CCM")));
  const RPM =
      myTasksActiveTab === "Nurse Tasks"
        ? false
        : Boolean(Number.parseInt(queryParams.get("RPM")));

  // The live timer value coming from the parent (patientDetail.js). Driven
  // by the header pill's TimeTracker. CCM or RPM picked by program scope.
  const liveSeconds = chatBotType === "CCM"
    ? (defaultTimer?.elapsedTimeCCM || 0)
    : (defaultTimer?.elapsedTimeRPM || 0);

  const noteTemplatesQuery = useQuery(
    ["note_templates", locationId],
    () => getNoteTemplates(locationId),
    { enabled: !!locationId, staleTime: 5 * 60 * 1000 }
  );
  const noteTemplates = noteTemplatesQuery?.data || [];

  const [selectedTemplate, setSelectedTemplate] = useState("");

  // Captured at save-click time so the success toast can show the
  // exact same breakdown the backend persisted (live timer + manual
  // offset = total). Re-deriving inside onSuccess would risk drifting
  // because the live timer keeps ticking between click and server
  // response.
  const lastSaveBreakdownRef = useRef(null);

  const monitoringMutation = useMutation(
    (sessionData) =>
      // Store under the PATIENT's location, not the viewer's current
      // location (SCRUM-157). `locationId` above stays current_loc for
      // note-template lookups, which are location-config, not patient data.
      createMonitoringSession(sessionData, patientId, getPatientLocationId()),
    {
      onSuccess: () => {
        resetForm();
        queryClient.invalidateQueries(["patient-notes"]);
        queryClient.invalidateQueries(["patient-monitoring-sessions"]);
        queryClient.invalidateQueries(["patientNotes"]);
        queryClient.invalidateQueries(["patient-statuses"]);
        queryClient.invalidateQueries(["global-statuses"]);

        const b = lastSaveBreakdownRef.current;
        if (b) {
          toast.custom(
            (t) => (
              <SaveSessionToast
                t={t}
                snapshotSeconds={b.snapshotSeconds}
                manualSeconds={b.manualSeconds}
                totalSeconds={b.totalSeconds}
                program={b.program}
              />
            ),
            { duration: 6000 }
          );
        } else {
          toast.success("Session added successfully");
        }

        resetTimer(true);
        sessionStorage.setItem("mte", true);

        // Dispatch trigger-save event to reset DailiesOORTracker in Readings Review
        window.dispatchEvent(new Event("trigger-save"));
      },
      onError: (error) => {
        toast.error("Could not add session");
      },
    }
  );

  const refineNoteMutation = useMutation(
    (payload) => refineNote(payload),
    {
      onSuccess: (refinedText) => {
        if (refinedText) {
          setNotes(refinedText);
          toast.success("Note refined");
        }
      },
      onError: (error) => {
        handleApiError(error);
      },
    }
  );

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleMonitoringSubmit = () => {
    const isCCM = chatBotType === "CCM";

    // Save math:
    //   engaged    → total = snapshot + manual (manual = 0 if user
    //                clicked but never typed).
    //   not engaged → total = current live timer value.
    // 'Engaged' = snapshot was captured, which happens on first focus.
    const engaged = snapshotValue !== null;
    const added = engaged ? manualAdded : 0;
    const total = engaged ? snapshotValue + added : liveSeconds;

    if (total <= 0) {
      return toast.error("Please select time");
    }

    if (!notes) {
      return toast.error("Add note for session before saving it.");
    }

    lastSaveBreakdownRef.current = {
      // Snapshot captured on first click. null when user never engaged.
      snapshotSeconds: engaged ? snapshotValue : null,
      // What the user typed — sums directly with snapshot in the toast.
      manualSeconds: engaged ? added : null,
      totalSeconds: total,
      program: isCCM ? "CCM" : "RPM",
    };

    const sessionData = {
      ...(isCCM
        ? { ccm_note: notes, total_ccm_seconds: total }
        : { rpm_note: notes, total_rpm_seconds: total }),
      made_compliance_call: complicanCall,
      left_voicemail: callCompleted,
      made_two_way_communication: twoWayCommunicationCall,
      created_at: toUTCISOString(selectedDate || undefined),
    };

    monitoringMutation.mutate(sessionData);
  };

  const resetForm = () => {
    setManualMin("");
    setManualSec("");
    setSnapshotValue(null);
    setSelectedDate(null);
    setNotes("");
    setCallCompleted(false);
    setComplianceCall(false);
    setTwoWayCommunicationCall(false);
    setCommunicationOutcome(null);
  };

  // The two boxes are the user's MANUAL ENTRY surface — not a running total.
  // Each shows exactly what was typed (the seconds box is NOT normalized, so
  // 80 stays "80" instead of rolling into the minutes box). They are summed
  // only at save time. manualAdded is the total manual seconds to add.
  const minutesVal = manualMin;
  const secondsVal = manualSec;
  const manualAdded =
    (parseInt(manualMin || "0", 10) || 0) * 60 +
    (parseInt(manualSec || "0", 10) || 0);

  // First-focus helper: capture the live timer into snapshotValue for
  // the chip and the save math. The field is NOT pre-filled — the
  // user's first keystroke starts the manual entry from 00.
  const snapshotIfNeeded = () => {
    if (snapshotValue === null) {
      setSnapshotValue(liveSeconds);
    }
  };

  const handleFocus = (e) => {
    snapshotIfNeeded();
    if (e?.target?.select) {
      requestAnimationFrame(() => {
        try { e.target.select(); } catch (_) { /* element may have blurred */ }
      });
    }
  };

  // If the user clicks into the field but leaves WITHOUT adding any manual
  // time (still 00:00), discard the snapshot so the heading chip and the nav
  // timer re-sync to the live value. Focus moving between the minutes and
  // seconds inputs of the same group is ignored (relatedTarget stays inside).
  const handleGroupBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    if (!manualAdded) {
      setSnapshotValue(null);
      setManualMin("");
      setManualSec("");
    }
  };

  // Each box stores raw digits independently (the user is typing the AMOUNT
  // TO ADD, not a running total). Non-digits are stripped and the entry is
  // capped at 4 digits. The seconds box deliberately accepts values >59
  // (e.g. 80, 120) and never rolls over into the minutes box; the two are
  // summed only at save time.
  const handleMinutesInput = (val) => {
    setManualMin((val || "").replace(/\D/g, "").slice(0, 4));
  };

  const handleSecondsInput = (val) => {
    setManualSec((val || "").replace(/\D/g, "").slice(0, 4));
  };

  const handleCallTypeCheck = (callType) => {
  switch (callType) {
    case "voicemail":
      setCallCompleted((prev) => {
        const next = !prev;

        setTwoWayCommunicationCall(false);
        setCommunicationOutcome(next ? "Left VoiceMail" : null);

        return next;
      });
      break;

    case "twoWay":
      setTwoWayCommunicationCall((prev) => {
        const next = !prev;

        setCallCompleted(false);
        setCommunicationOutcome(next ? "Two-Way Communication" : null);

        return next;
      });
      break;

    default:
      break;
  }
};

  useEffect(() => {
    if (RPM) {
      setNoteType("rpm_note");
    } else {
      setNoteType("ccm_note");
    }
  }, [CCM, RPM]);

  const removeCallTypeTexts = (text) => {
    if (!text) return "";
    const callTypeTexts = [
      "Left Voice Mail",
      "Compliance Call",
      "Two Way Communication",
    ];
    let cleaned = text;
    callTypeTexts.forEach((callText) => {
      const escapedText = callText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      cleaned = cleaned.replace(new RegExp(`^${escapedText}$`, "gm"), "");
      cleaned = cleaned.replace(new RegExp(`\n${escapedText}$`, "gm"), "");
      cleaned = cleaned.replace(new RegExp(`^${escapedText}\n`, "gm"), "");
      cleaned = cleaned.replace(new RegExp(`\n${escapedText}\n`, "gm"), "\n");
    });
    return cleaned.trim();
  };

  useEffect(() => {
    setNotes((prev) => {
      const base = removeCallTypeTexts(prev);
      const callTexts = [];
      if (callCompleted) callTexts.push('Left Voice Mail');
      if (complicanCall) callTexts.push('Compliance Call');
      if (twoWayCommunicationCall) callTexts.push('Two Way Communication');
      if (callTexts.length) {
        return base ? `${base}\n${callTexts.join('\n')}` : callTexts.join('\n');
      }
      return base;
    });
  }, [callCompleted, complicanCall, twoWayCommunicationCall]);

  const content = (
    <div>
      <section className={styles.monitoring_actions_container}>
        <div className={styles.monitoring_actions_header}>
          <div className={styles.time_options}>
            <div
              className={clsx(styles.time_option, callCompleted && styles.time_option_active)}
              onClick={() => handleCallTypeCheck("voicemail")}
            >
              Left Voicemail
            </div>
            <div
              className={clsx(styles.time_option, twoWayCommunicationCall && styles.time_option_active)}
              onClick={() => handleCallTypeCheck("twoWay")}
            >
              Two way communication
            </div>
          </div>
          <div className={styles.time_input_header}>
            <span className={styles.time_label}>
              Add Time
              <Tooltip
                content="Time you enter manually — added on top of the live timer snapshot when you Save."
                position="top"
              >
                <svg
                  className={styles.time_label_info}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  role="img"
                  aria-label="About manual time"
                >
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="11" x2="12" y2="16" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </Tooltip>
            </span>
            <div className={styles.timeInputsRow} onBlur={handleGroupBlur}>
              <input
                ref={minutesRef}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={minutesVal}
                onChange={(e) => handleMinutesInput(e.target.value)}
                onFocus={handleFocus}
                className={clsx(styles.timeInput, styles.timeInputMinutes, "no-arrows")}
                placeholder="MM"
                aria-label="Minutes"
              />
              :
              <input
                ref={secondsRef}
                type="text"
                inputMode="numeric"
                pattern="\d*"
                value={secondsVal}
                onChange={(e) => handleSecondsInput(e.target.value)}
                onFocus={handleFocus}
                className={clsx(styles.timeInput, "no-arrows")}
                placeholder="SS"
                aria-label="Seconds"
              />
            </div>
          </div>

        </div>


        <section style={{ width: "100%" }}>
          {/* Date Picker Row */}
             <div style={{ marginBottom: '16px' }}>
                <DatePicker
                  selectedDate={selectedDate || curr}
                  onDateSelect={handleDateChange}
                  showTime={true}
                  disableFutureDates={true}
                  onCancelSelect={() => setSelectedDate(null)}
                  placeholder="Select Date"
                  inputStyle={{
                    borderRadius: "4px",
                    padding: "8px",
                    backgroundColor: "#F8FAFD",
                    // Figma 357:1613 — DM Sans Regular 14px #0d1139.
                    fontFamily: 'var(--font-display, "DM Sans"), sans-serif',
                    fontSize: "14px",
                    fontWeight: 400,
                    color: "#0d1139",
                    fontVariationSettings: "'opsz' 14",
                  }}
                />
            </div>
          <div>
            <Select
              value={selectedTemplate}
                onChange={(v) => {
                  if (v) {
                    setNotes((prev) => (prev ? `${prev}\n${v}` : v));
                  } else {
                    setNotes('');
                    setCallCompleted(false);
                    setComplianceCall(false);
                    setTwoWayCommunicationCall(false);
                    setCommunicationOutcome(null);
                  }
                  setSelectedTemplate('');
                }}
              disabled={noteTemplatesQuery.isLoading}
            >
              <SelectTrigger style={{ borderRadius: "4px", padding: "8px", fontSize: "12px" ,backgroundColor: "#F8FAFD"}}>
                <SelectValue placeholder="Pre-completed note" />
              </SelectTrigger>
              <SelectContent searchField>
                <SelectItem key="" value="">
                  --None--
                </SelectItem>
                {noteTemplates?.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.note_template_text}>
                    {tpl.note_template_text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>
      </section>
      <section className={styles.monitoring_content}>
        <div className={styles.body_left}>
          <div className={styles.notes_wrapper}>
            <NoteAutocompleteTextarea
              className={styles.notes_input}
              style={{
                borderRadius: "4px",
                backgroundColor: "#F8FAFD",
                opacity: refineNoteMutation.isLoading ? 0.7 : 1,
              }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Enter text"
              readOnly={refineNoteMutation.isLoading}
            />
            {(() => {
              const isRefining = refineNoteMutation.isLoading;
              const isDisabled = notes.trim().length === 0 || isRefining;
              return (
                <button
                  type="button"
                  className={styles.notes_copilot_btn}
                  disabled={isDisabled}
                  title={isDisabled && !isRefining ? "Type a note to enable refine" : "Refine note"}
                  aria-label="Refine note"
                  style={{
                    opacity: isDisabled ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    border: "none",
                    padding: 0,
                  }}
                  onClick={() => {
                    if (isDisabled) return;
                    refineNoteMutation.mutate({ text: notes, communication_outcome: communicationOutcome });
                  }}
                >
                  {isRefining ? (
                    <FontAwesomeIcon icon={faSpinner} spin style={{ color: "#ffffff", fontSize: 16 }} />
                  ) : (
                    <img src={magicIcon} alt="" width="18" height="18" />
                  )}
                </button>
              );
            })()}
          </div>
        </div>
        <div className={styles.body_right}>
          <Button
            loading={monitoringMutation.isLoading}
            onClick={() => handleMonitoringSubmit()}
            className={styles.active_button}
            style={{ width: "100%", backgroundColor: "#48A6C4", borderColor: "#48A6C4" , borderRadius: "6px"}}
          >
            Save
          </Button>
        </div>
      </section>

    </div>
  );

  // Subtle chip rendered at the right edge of the section heading.
  // Before the user engages the field, it shows the live timer's
  // ticking value ("Live MM:SS"). After they click the field and a
  // snapshot is captured, it freezes at the captured value
  // ("Snapshot MM:SS") so the user can always see what was originally
  // grabbed from the timer, independent of any edits they make.
  const isSnapshotted = snapshotValue !== null;
  const chipSourceSeconds = isSnapshotted ? snapshotValue : (liveSeconds || 0);
  const chipMinutes = String(Math.floor(chipSourceSeconds / 60)).padStart(2, "0");
  const chipSeconds = String(chipSourceSeconds % 60).padStart(2, "0");
  const chipValue = `${chipMinutes}:${chipSeconds}`;
  const chipLabel = isSnapshotted ? "Snapshot" : "Live";

  return (
    <div>
      <div className={styles.heading_row}>
        <p className={styles.comp_heading_text}>{title}</p>
        <div className={styles.heading_actions}>
          {showNotesHistory && (
            <Tooltip content="Notes History" position="left">
              <button
                type="button"
                ref={notesHistoryBtnRef}
                className={styles.notes_history_btn}
                onClick={() => setIsNotesHistoryOpen(true)}
                aria-label="View notes history"
              >
                <img src="/icons/clipboard-clock.svg" alt="" width="16" height="16" />
              </button>
            </Tooltip>
          )}
          <span
            className={clsx(
              styles.live_chip,
              isSnapshotted && styles.live_chip_paused
            )}
            aria-label={`${chipLabel} ${chipValue}`}
            title={
              isSnapshotted
                ? "Captured at click. The live timer in the nav keeps ticking."
                : "Live timer. Click the field below to capture this value."
            }
          >
            <svg
              className={styles.live_chip_icon}
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <polyline points="12 7 12 12 15.5 14" />
            </svg>
            <span className={styles.live_chip_label}>{chipLabel}</span>
            <span className={styles.live_chip_value}>{chipValue}</span>
          </span>
        </div>
      </div>
      {content}
      {showNotesHistory && (
        <NotesHistoryDrawer
          isOpen={isNotesHistoryOpen}
          onClose={() => {
            setIsNotesHistoryOpen(false);
            notesHistoryBtnRef.current?.focus();
          }}
          chatBotType={chatBotType}
          patientId={patientId}
          locationId={locationId}
        />
      )}
    </div>
  );
};

export default Monitoring;
